// Build a scene by dragging it, instead of typing coordinates.
//
// A scene payload is around 130 lines of JSON — seven players with an x
// and a y each, then a frame apiece carrying moves, a ball, a line and a
// highlight list. Authoring one by hand meant guessing numbers and
// rebuilding the app to find out whether the striker landed where you
// meant. That is the same bottleneck the admin tools were built to
// remove, reappearing in the newest feature.
//
// The one idea that makes this work: dragging on frame 1 moves a player's
// STARTING position; dragging on any later frame writes a move for that
// frame only. The editor is therefore showing you the same thing the
// learner will see at that point in the play, and you edit it in place.
import { useRef, useState } from "react";
import {
  Circle,
  Copy,
  Minus,
  Plus,
  Trash2,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CHALK, PITCH_COLOR as C } from "./geometry";
import { stateAt, type ScenePayload, type ScenePlayer } from "./ScenePreview";

const PITCH_W = 300;
const PITCH_H = PITCH_W * 1.5;
const TOKEN = 28;

type Pos = { x: number; y: number };

const clamp = (v: number) => Math.max(2, Math.min(98, Math.round(v * 10) / 10));

// The ball rides the same drag machinery as a player rather than growing
// a second one: one pointer handler, one place to get it wrong.
const BALL_DRAG = "__ball__";

export default function SceneEditor({
  payload,
  onChange,
}: {
  payload: ScenePayload;
  onChange: (next: ScenePayload) => void;
}) {
  const players = payload.players ?? [];
  const frames = payload.frames ?? [];

  const [frameIndex, setFrameIndex] = useState(0);
  const [dragging, setDragging] = useState<string | null>(null);
  const pitchRef = useRef<HTMLDivElement>(null);

  const frame = frames[frameIndex];
  const state = stateAt(payload, frameIndex);

  const patch = (next: Partial<ScenePayload>) => onChange({ ...payload, ...next });

  const patchFrame = (i: number, next: Record<string, unknown>) =>
    patch({
      frames: frames.map((f, j) => (j === i ? { ...f, ...next } : f)),
    });

  // Where a drag writes to is the whole design. On the first frame it is
  // the player's starting position; after that it is a move belonging to
  // the frame you are looking at, so earlier frames keep what they had.
  const placePlayer = (id: string, pos: Pos) => {
    if (frameIndex === 0) {
      patch({
        players: players.map((p) => (p.id === id ? { ...p, ...pos } : p)),
      });
      return;
    }
    patchFrame(frameIndex, { moves: { ...(frame?.moves ?? {}), [id]: pos } });
  };

  // Dragging the ball always writes a loose position on THIS frame,
  // which detaches it from a player if it was at his feet. That is the
  // intent: you reach for the ball when you want it somewhere the
  // player is not — a pass in flight, a corner sitting on the spot.
  const placeBall = (pos: Pos) => patchFrame(frameIndex, { ball: pos });

  const pointerPos = (e: React.PointerEvent): Pos | null => {
    const rect = pitchRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return {
      x: clamp(((e.clientX - rect.left) / rect.width) * 100),
      y: clamp(((e.clientY - rect.top) / rect.height) * 100),
    };
  };

  const addPlayer = (team: "blue" | "red") => {
    // Ids are generated. They are referenced by moves, the ball, lines
    // and the answer key — an author editing one by hand can only break
    // those links.
    let n = players.length + 1;
    const taken = new Set(players.map((p) => p.id));
    while (taken.has(`p${n}`)) n++;
    patch({
      players: [
        ...players,
        {
          id: `p${n}`,
          team,
          label: team === "blue" ? "ST" : "CB",
          x: 50,
          y: team === "blue" ? 60 : 40,
        },
      ],
    });
  };

  const removePlayer = (id: string) => {
    patch({
      players: players.filter((p) => p.id !== id),
      // Everything that pointed at him has to go too, or the payload
      // fails validation with a message about a player that no longer
      // exists — technically correct and useless to act on.
      frames: frames.map((f) => {
        const moves = { ...(f.moves ?? {}) };
        delete moves[id];
        return {
          ...f,
          moves,
          ball: f.ball?.at === id ? undefined : f.ball,
          lines: (f.lines ?? []).filter((l) => l.throughPlayer !== id),
          highlight: (f.highlight ?? []).filter((h) => h !== id),
        };
      }),
      correctPlayerIds: (payload.correctPlayerIds ?? []).filter((p) => p !== id),
    });
  };

  const addFrame = (copyCurrent: boolean) => {
    const blank = copyCurrent && frame
      ? { ...structuredClone(frame), caption: `${frame.caption ?? ""} (copy)` }
      : { caption: "" };
    const next = [...frames];
    next.splice(frameIndex + 1, 0, blank);
    patch({ frames: next });
    setFrameIndex(frameIndex + 1);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start gap-5">
        {/* ── The pitch ──────────────────────────────────────────── */}
        <div className="space-y-2">
          <div
            ref={pitchRef}
            onPointerMove={(e) => {
              if (!dragging) return;
              const pos = pointerPos(e);
              if (!pos) return;
              if (dragging === BALL_DRAG) placeBall(pos);
              else placePlayer(dragging, pos);
            }}
            onPointerUp={() => setDragging(null)}
            onPointerLeave={() => setDragging(null)}
            className="relative select-none overflow-hidden rounded-[14px] border"
            style={{
              width: PITCH_W,
              height: PITCH_H,
              background: C.boardPitchBg,
              borderColor: "rgba(255,255,255,0.08)",
              touchAction: "none",
            }}
          >
            <svg
              className="pointer-events-none absolute inset-0 h-full w-full"
              viewBox={CHALK.viewBox}
              preserveAspectRatio="none"
            >
              <rect
                x={CHALK.outer.x}
                y={CHALK.outer.y}
                width={CHALK.outer.w}
                height={CHALK.outer.h}
                stroke={C.chalk}
                strokeWidth={0.6}
                fill="none"
              />
              <line
                x1={CHALK.outer.x}
                y1={CHALK.halfway.y}
                x2={CHALK.outer.x + CHALK.outer.w}
                y2={CHALK.halfway.y}
                stroke={C.chalk}
                strokeWidth={0.6}
              />
              <circle
                cx={CHALK.centreCircle.cx}
                cy={CHALK.centreCircle.cy}
                r={CHALK.centreCircle.r}
                stroke={C.chalk}
                strokeWidth={0.6}
                fill="none"
              />
              {CHALK.boxes.map((b, i) => (
                <rect
                  key={i}
                  x={b.x}
                  y={b.y}
                  width={b.w}
                  height={b.h}
                  stroke={C.chalk}
                  strokeWidth={0.6}
                  fill="none"
                />
              ))}
            </svg>

            {state.lines.map((line, i) => {
              const y =
                line.throughPlayer != null
                  ? state.positions.get(line.throughPlayer)?.y
                  : line.y;
              if (typeof y !== "number") return null;
              const colour =
                line.tone === "warn"
                  ? C.gold
                  : line.tone === "good"
                    ? C.correct
                    : "rgba(255,255,255,0.55)";
              return (
                <div
                  key={i}
                  className="pointer-events-none absolute left-0 right-0"
                  style={{ top: `${y}%` }}
                >
                  <div style={{ height: 2, background: colour }} />
                  {line.label && (
                    <span
                      className="absolute right-1 mt-0.5 rounded px-1 text-[9px] font-bold"
                      style={{ background: "rgba(8,20,14,0.9)", color: colour }}
                    >
                      {line.label}
                    </span>
                  )}
                </div>
              );
            })}

            {players.map((p) => {
              const pos = state.positions.get(p.id) ?? { x: p.x, y: p.y };
              const lit = (frame?.highlight ?? []).includes(p.id);
              const movedHere = frameIndex > 0 && !!frame?.moves?.[p.id];
              return (
                <div
                  key={p.id}
                  onPointerDown={(e) => {
                    e.preventDefault();
                    setDragging(p.id);
                  }}
                  title={`${p.id} — drag to move`}
                  className="absolute flex cursor-grab items-center justify-center rounded-full font-extrabold active:cursor-grabbing"
                  style={{
                    left: `${pos.x}%`,
                    top: `${pos.y}%`,
                    width: TOKEN,
                    height: TOKEN,
                    marginLeft: -TOKEN / 2,
                    marginTop: -TOKEN / 2,
                    background: p.team === "blue" ? C.blue : C.red,
                    color: p.team === "blue" ? C.blueInk : C.redInk,
                    fontSize: 10,
                    border: lit ? `3px solid ${C.gold}` : undefined,
                    // A dashed outline marks a player this frame actually
                    // moves, so an author can see at a glance which of
                    // them this frame is about.
                    outline: movedHere ? `2px dashed ${C.gold}` : undefined,
                    outlineOffset: 2,
                  }}
                >
                  {p.label}
                </div>
              );
            })}

            {state.ball && (
              <BallDot
                ball={state.ball}
                positions={state.positions}
                onGrab={() => setDragging(BALL_DRAG)}
              />
            )}
          </div>

          <p className="w-[300px] text-[11px] text-muted-foreground">
            {frameIndex === 0
              ? "Dragging here sets where players START the scene."
              : `Dragging here writes a move for frame ${frameIndex + 1} only. Earlier frames keep what they had.`}
          </p>
        </div>

        {/* ── Controls ───────────────────────────────────────────── */}
        <div className="min-w-[280px] flex-1 space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">
                Frame {frameIndex + 1} of {frames.length || 1}
              </Label>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 cursor-pointer gap-1 px-2 text-xs"
                  onClick={() => addFrame(true)}
                  title="Duplicate this frame"
                >
                  <Copy className="h-3 w-3" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 cursor-pointer gap-1 px-2 text-xs"
                  onClick={() => addFrame(false)}
                >
                  <Plus className="h-3 w-3" />
                  Frame
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={frames.length <= 1}
                  className="h-7 cursor-pointer px-2 text-xs text-red-600"
                  onClick={() => {
                    patch({ frames: frames.filter((_, i) => i !== frameIndex) });
                    setFrameIndex(Math.max(0, frameIndex - 1));
                  }}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap gap-1">
              {frames.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setFrameIndex(i)}
                  className={
                    "h-7 w-7 cursor-pointer rounded text-[11px] font-bold transition-colors " +
                    (i === frameIndex
                      ? "bg-emerald-600 text-white"
                      : "bg-muted text-muted-foreground hover:bg-muted/70")
                  }
                >
                  {i + 1}
                </button>
              ))}
            </div>

            <textarea
              value={frame?.caption ?? ""}
              onChange={(e) => patchFrame(frameIndex, { caption: e.target.value })}
              rows={2}
              placeholder="What is happening in this frame? One sentence."
              className="w-full resize-y rounded-md border bg-transparent p-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500/40"
            />
          </div>

          {/* Ball */}
          <div className="space-y-1.5 rounded-lg border p-2.5">
            <Label className="flex items-center gap-1.5 text-xs">
              <Circle className="h-3 w-3" /> Ball
            </Label>
            <Select
              value={frame?.ball?.at ?? (frame?.ball ? "__free__" : "__none__")}
              onValueChange={(v) => {
                if (v === "__none__") return patchFrame(frameIndex, { ball: undefined });
                if (v === "__free__")
                  return patchFrame(frameIndex, { ball: { x: 50, y: 50 } });
                patchFrame(frameIndex, { ball: { at: v } });
              }}
            >
              <SelectTrigger className="h-8 cursor-pointer text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">
                  Unchanged from the frame before
                </SelectItem>
                <SelectItem value="__free__">Loose on the pitch</SelectItem>
                {players.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    At {p.label}&apos;s feet
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {frame?.ball && !frame.ball.at && (
              <div className="flex items-center gap-2">
                <NumberField
                  label="x"
                  value={frame.ball.x ?? 50}
                  onChange={(x) => patchFrame(frameIndex, { ball: { ...frame.ball, x } })}
                />
                <NumberField
                  label="y"
                  value={frame.ball.y ?? 50}
                  onChange={(y) => patchFrame(frameIndex, { ball: { ...frame.ball, y } })}
                />
              </div>
            )}
          </div>

          {/* Lines */}
          <div className="space-y-2 rounded-lg border p-2.5">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1.5 text-xs">
                <Minus className="h-3 w-3" /> Lines
              </Label>
              <Button
                size="sm"
                variant="outline"
                className="h-6 cursor-pointer px-2 text-[11px]"
                onClick={() =>
                  patchFrame(frameIndex, {
                    lines: [
                      ...(frame?.lines ?? []),
                      { throughPlayer: players[0]?.id, label: "", tone: "warn" },
                    ],
                  })
                }
              >
                <Plus className="h-3 w-3" /> Add
              </Button>
            </div>
            {(frame?.lines ?? []).length === 0 && (
              <p className="text-[11px] text-muted-foreground">
                None in this frame. Lines carry forward from the frame
                before unless you set them here.
              </p>
            )}
            {(frame?.lines ?? []).map((line, i) => {
              const update = (next: Record<string, unknown>) =>
                patchFrame(frameIndex, {
                  lines: (frame?.lines ?? []).map((l, j) =>
                    j === i ? { ...l, ...next } : l
                  ),
                });
              return (
                <div key={i} className="space-y-1.5 rounded border p-2">
                  <Select
                    value={line.throughPlayer ?? "__fixed__"}
                    onValueChange={(v) =>
                      v === "__fixed__"
                        ? update({ throughPlayer: undefined, y: 40 })
                        : update({ throughPlayer: v, y: undefined })
                    }
                  >
                    <SelectTrigger className="h-8 cursor-pointer text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {players.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          Pinned to {p.label} — moves when he does
                        </SelectItem>
                      ))}
                      <SelectItem value="__fixed__">
                        Fixed height on the pitch
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {line.throughPlayer == null && (
                    <NumberField
                      label="height"
                      value={line.y ?? 40}
                      onChange={(y) => update({ y })}
                    />
                  )}
                  <Input
                    className="h-8 text-xs"
                    value={line.label ?? ""}
                    onChange={(e) => update({ label: e.target.value })}
                    placeholder="Label, e.g. The offside line"
                  />
                  <div className="flex items-center gap-2">
                    <Select
                      value={line.tone ?? "neutral"}
                      onValueChange={(v) => update({ tone: v })}
                    >
                      <SelectTrigger className="h-7 flex-1 cursor-pointer text-[11px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="warn">Gold — the live line</SelectItem>
                        <SelectItem value="good">Green</SelectItem>
                        <SelectItem value="neutral">Grey — a past line</SelectItem>
                      </SelectContent>
                    </Select>
                    <button
                      type="button"
                      onClick={() =>
                        patchFrame(frameIndex, {
                          lines: (frame?.lines ?? []).filter((_, j) => j !== i),
                        })
                      }
                      className="cursor-pointer text-red-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Cast */}
          <div className="space-y-2 rounded-lg border p-2.5">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1.5 text-xs">
                <Users className="h-3 w-3" /> Players
              </Label>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 cursor-pointer px-2 text-[11px]"
                  onClick={() => addPlayer("blue")}
                >
                  + Blue
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 cursor-pointer px-2 text-[11px]"
                  onClick={() => addPlayer("red")}
                >
                  + Red
                </Button>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Blue is the team being taught, red the opposition. Tick a
              player to spotlight him in this frame.
            </p>
            {players.map((p) => {
              const lit = (frame?.highlight ?? []).includes(p.id);
              return (
                <div key={p.id} className="flex items-center gap-2">
                  <button
                    type="button"
                    title="Spotlight in this frame"
                    onClick={() =>
                      patchFrame(frameIndex, {
                        highlight: lit
                          ? (frame?.highlight ?? []).filter((h) => h !== p.id)
                          : [...(frame?.highlight ?? []), p.id],
                      })
                    }
                    className={
                      "h-5 w-5 shrink-0 cursor-pointer rounded border-2 " +
                      (lit ? "border-amber-500 bg-amber-400" : "border-muted-foreground/40")
                    }
                  />
                  <span
                    className="h-3 w-3 shrink-0 rounded-full"
                    style={{ background: p.team === "blue" ? C.blue : C.red }}
                  />
                  <Input
                    className="h-7 w-20 text-xs"
                    value={p.label}
                    onChange={(e) =>
                      patch({
                        players: players.map((x) =>
                          x.id === p.id ? { ...x, label: e.target.value } : x
                        ),
                      })
                    }
                  />
                  <code className="flex-1 truncate text-[10px] text-muted-foreground">
                    {p.id}
                  </code>
                  <button
                    type="button"
                    onClick={() => removePlayer(p.id)}
                    className="cursor-pointer text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
            {players.length === 0 && (
              <p className="text-[11px] text-amber-600">
                A scene needs at least one blue player.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-[10px] text-muted-foreground">{label}</span>
      <Input
        className="h-7 w-16 text-xs"
        value={String(value)}
        inputMode="decimal"
        onChange={(e) => {
          const n = Number(e.target.value);
          if (!Number.isNaN(n)) onChange(clamp(n));
        }}
      />
    </div>
  );
}

function BallDot({
  ball,
  positions,
  onGrab,
}: {
  ball: { at?: string; x?: number; y?: number };
  positions: Map<string, Pos>;
  onGrab: () => void;
}) {
  const carried = ball.at ? positions.get(ball.at) : undefined;
  const x = carried ? carried.x + 3.5 : (ball.x ?? 50);
  const y = carried ? carried.y + 3 : (ball.y ?? 50);
  return (
    <span
      onPointerDown={(e) => {
        // Without this the press falls through to whatever is under the
        // ball, which with the ball at a player's feet is the player.
        e.preventDefault();
        e.stopPropagation();
        onGrab();
      }}
      title={
        ball.at
          ? "Drag to drop the ball loose — this takes it off the player"
          : "Drag to move the ball"
      }
      className="absolute cursor-grab active:cursor-grabbing"
      style={{
        left: `${x}%`,
        top: `${y}%`,
        width: 16,
        height: 16,
        marginLeft: -8,
        marginTop: -8,
      }}
    >
      {/* The same ball the lesson shows, drawn small: white sphere,
          outlined centre panel. A crosshair icon read as a target. */}
      <svg width={16} height={16} viewBox="0 0 24 24">
        <circle cx={12} cy={12} r={10.5} fill="#fff" stroke="#0B1F14" strokeWidth={1.4} />
        <polygon
          points="12,7.4 16.38,10.58 14.71,15.72 9.29,15.72 7.62,10.58"
          fill="none"
          stroke="#111820"
          strokeWidth={1.2}
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

export type { ScenePlayer };
